import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { toast } from 'react-toastify';
import { useRealtime } from './useRealtime';
import { useCurrentUser } from '@features/user/hooks/useAuth';
import { NewMessageToast } from '@features/conversations/components/NewMessageToast';
import { buildMessagePreview, decideMessageNotification } from '@features/conversations/services/messageNotification';
import { showToast } from '@features/ui-atoms/services/showToast';
import { ToastVariants } from '@features/ui-atoms/components/Toast';
import getRoute from '@services/routeList';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@services/api/queries';
import type { ConversationMessageNewEvent, ConversationReadEvent } from '@services/websocket/RealtimeManager';

/** How long the new-message toast stays before it dismisses itself. */
const NEW_MESSAGE_TOAST_DURATION_MS = 6000;

/**
 * Global notifications hook — handles WebSocket events app-wide.
 * Keeps conversation list (unread counts, last message) in sync.
 */
export const useGlobalNotifications = () => {
  const { on, isConnected, joinRoom, leaveRoom } = useRealtime();
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { t } = useTranslation('common');

  // Keep a ref so event handlers always read the current route without
  // needing router in effect deps (which would re-subscribe on every navigation)
  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  });

  // Subscribe to all user conversation rooms present in the cache at connect time
  useEffect(() => {
    if (!isConnected || !currentUser) return;

    const conversationIds = new Set<string>();
    queryClient
      .getQueriesData<{ items?: Array<{ id?: string }> }>({ queryKey: [QUERY_KEYS.CONVERSATIONS] })
      .forEach(([, data]) => {
        data?.items?.forEach(conv => {
          if (conv.id) conversationIds.add(conv.id);
        });
      });

    conversationIds.forEach(id => joinRoom('conversation', id));
    return () => conversationIds.forEach(id => leaveRoom('conversation', id));
  }, [isConnected, currentUser, joinRoom, leaveRoom, queryClient]);

  // Handle new messages — update conversation list (unread count + last message)
  useEffect(() => {
    if (!isConnected || !currentUser) return;

    const cleanups: (() => void)[] = [];

    cleanups.push(
      on<ConversationMessageNewEvent>('conversation:message:new', event => {
        const { pathname, query } = routerRef.current;
        const openConversationId =
          pathname.includes('/messages') && typeof query.conversationId === 'string' ? query.conversationId : undefined;

        const { incrementUnread: shouldIncrementUnread, showToast: shouldToast } = decideMessageNotification({
          currentUserId: currentUser.id,
          authorId: event.message.author.id,
          conversationId: event.conversationId,
          openConversationId,
        });

        if (shouldToast) {
          const preview = buildMessagePreview(event.message.content);
          const { firstName, lastName } = event.message.author;
          // One toast per conversation: a second message from the same sender replaces
          // it, while another sender raises a toast of their own.
          const toastId = `conversation-message-${event.conversationId}`;

          showToast({
            variant: ToastVariants.INFO,
            title: t('conversation.notification.title', { name: `${firstName} ${lastName}`.trim() }),
            toastId,
            autoClose: NEW_MESSAGE_TOAST_DURATION_MS,
            children: (
              <NewMessageToast
                preview={preview || t('conversation.notification.noPreview')}
                viewLabel={t('conversation.notification.view')}
                onView={() => {
                  toast.dismiss(toastId);
                  void routerRef.current.push(getRoute.messages(event.conversationId));
                }}
              />
            ),
          });
        }

        // A message can be the first of a conversation the client has never seen —
        // nothing to patch in the cache, the list itself has to be refetched.
        const isKnownConversation = queryClient
          .getQueriesData<{ items?: Array<{ id?: string }> }>({ queryKey: [QUERY_KEYS.CONVERSATIONS] })
          .some(([, data]) => data?.items?.some(conversation => conversation.id === event.conversationId));

        if (!isKnownConversation) {
          void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONVERSATIONS] });
          return;
        }

        queryClient.setQueriesData<{ items?: Array<Record<string, unknown>> }>(
          { queryKey: [QUERY_KEYS.CONVERSATIONS], exact: false },
          oldData => {
            if (!oldData?.items) return oldData;
            return {
              ...oldData,
              items: oldData.items.map(conversation =>
                conversation.id === event.conversationId
                  ? {
                      ...conversation,
                      lastMessage: {
                        id: event.message.id,
                        content: event.message.content,
                        createdAt: event.message.createdAt,
                        author: event.message.author,
                      },
                      lastMessageTimestamp: new Date(event.message.createdAt),
                      unread: shouldIncrementUnread ? ((conversation.unread as number) || 0) + 1 : conversation.unread,
                    }
                  : conversation,
              ),
            };
          },
        );
      }),
    );

    // Handle read receipts — clear unread count when current user reads a conversation
    cleanups.push(
      on<ConversationReadEvent>('conversation:read', event => {
        if (event.userId !== currentUser.id) return;
        queryClient.setQueriesData<{ items?: Array<Record<string, unknown>> }>(
          { queryKey: [QUERY_KEYS.CONVERSATIONS], exact: false },
          oldData => {
            if (!oldData?.items) return oldData;
            return {
              ...oldData,
              items: oldData.items.map(conversation =>
                conversation.id === event.conversationId ? { ...conversation, unread: 0 } : conversation,
              ),
            };
          },
        );
      }),
    );

    return () => cleanups.forEach(cleanup => cleanup());
  }, [isConnected, currentUser, on, queryClient, t]);
};
